import React from "react";
import Link from "next/link";
import { cva, type VariantProps } from "class-variance-authority";
import { Button } from "../buttons";
import { Image } from "../Image";
import type { ImageProps } from "@/types/image";
import type { ButtonConfig } from "@/types/common";
import { BorderGlow } from "@/components/animations";
import { Heading, WysiwygHeading } from "../Heading";
import { Wysiwyg } from "../Wysiwyg";

const serviceCardVariants = cva(
  "relative flex flex-col rounded-lg overflow-hidden aspect-6/7 cardRef outline-offset-0 focus-visible:outline-2 focus-visible:outline-offset-2 outline-transparent",
  {
    variants: {
      variant: {
        "full-color": "p-md justify-end",
        "full-image": "p-md justify-end",
        "image-top": "justify-start",
      },
      color: {
        none: "",
        yellow: "bg-yellow text-black",
        "purple-dark": "bg-purple-dark text-white",
      },
    },
    defaultVariants: {
      variant: "full-color",
      color: "yellow",
    },
  }
);

const contentVariants = cva("flex flex-col relative z-10", {
  variants: {
    variant: {
      "full-color": "",
      "full-image": "",
      "image-top": "p-md",
    },
  },
  defaultVariants: {
    variant: "full-color",
  },
});

export interface ServiceCardProps extends VariantProps<
  typeof serviceCardVariants
> {
  topline?: string | React.ReactNode;
  headline: string | React.ReactNode;
  text: string | React.ReactNode;
  image?: ImageProps;
  button?: ButtonConfig;
  className?: string;
}

/**
 * ServiceCard component for displaying services with different layout variants.
 *
 * Variants:
 * - full-color: Solid background color with text and button at the bottom.
 * - full-image: Background image with overlay and text/button at the bottom.
 * - image-top: Image at the top, background color with text/button at the bottom.
 */
export const ServiceCard: React.FC<ServiceCardProps> = ({
  variant,
  color,
  topline,
  headline,
  text,
  image,
  button,
  className = "",
}) => {
  const isFullImage = variant === "full-image";
  const isImageTop = variant === "image-top";

  // Create a type-safe mapping for the variants
  const effectiveColor = isFullImage ? "purple-dark" : (color ?? "yellow");

  return (
    <BorderGlow
      className="rounded-lg"
      color="yellow"
      backgroundColor={"transparent"}
      edgeSensitivity={10}
      glowIntensity={1}
      glowRadius={32}
    >
      <ServiceCardWrapper
        button={button}
        className={serviceCardVariants({
          variant,
          color: effectiveColor as ServiceCardProps["color"],
          className,
        })}
      >
        {/* Background Image for full-image variant */}
        {isFullImage && image && (
          <>
            <div className="absolute inset-0 z-0">
              <Image
                {...image}
                fill
                className="object-cover"
                alt={image.alt || ""}
              />
              <div className="absolute inset-0 bg-black/40" />
            </div>
          </>
        )}

        {/* Top Image for image-top variant */}
        {isImageTop && image && (
          <div className="relative flex-1 w-full min-h-0">
            <Image
              {...image}
              fill
              className="object-cover"
              alt={image.alt || ""}
            />
          </div>
        )}

        <div className={contentVariants({ variant })}>
          {topline && (
            <Wysiwyg
              className="text-sm font-regular uppercase tracking-wider block mb-1"
              html={topline as string}
              mode="body"
            />
          )}
          <WysiwygHeading
            tag={"h3"}
            style={"h4"}
            className="leading-tight mb-3"
            html={headline as string}
          />
          {!isImageTop && (
            <Wysiwyg
              className="text-md line-clamp-3"
              html={text as string}
              mode="body"
            />
          )}
          {button && (
            <div className="mt-sm">
              <Button
                {...button}
                isDummy={true}
                variant={button.variant ?? "secondary"}
                color={
                  button.color ??
                  (effectiveColor === "purple-dark" ? "yellow" : "purple")
                }
              />
            </div>
          )}
        </div>
      </ServiceCardWrapper>
    </BorderGlow>
  );
};

const ServiceCardWrapper = ({
  button,
  ...props
}: {
  children: React.ReactNode;
  className: string;
  button?: ButtonConfig;
}) => {
  if (!button || !button.link) return <div {...props} />;
  return (
    <Link
      {...props}
      href={button.link.href}
      target={button.link.target}
      rel={button.link.target === "_blank" ? "noopener noreferrer" : undefined}
      aria-label={button.link.ariaLabel ?? button.label?.toString()}
    />
  );
};
